import { useState, useCallback, useEffect, useRef } from 'react';
import { useGoogleDriveAuth } from '../../hooks/useGoogleDriveAuth';
import {
  listFiles,
  downloadFile,
  type DriveFile,
} from '../../services/googleDriveService';
import {
  checkImportConflictsFromString,
  importDataFromString,
  type ImportMode,
} from '../../services/exportService';
import type { ImportResult } from '../../types';
import { DialogShell } from './DialogShell';

interface GoogleDriveLoadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

type DialogState =
  | { step: 'auth' }
  | { step: 'loading' }
  | { step: 'file-list'; files: DriveFile[] }
  | {
      step: 'conflict';
      result: ImportResult;
      fileContent: string;
    }
  | { step: 'importing' }
  | { step: 'error'; message: string };

export function GoogleDriveLoadDialog({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: GoogleDriveLoadDialogProps) {
  const {
    isAuthenticated,
    isAuthenticating,
    error: authError,
    authenticate,
  } = useGoogleDriveAuth();
  const [state, setState] = useState<DialogState>(() =>
    isAuthenticated ? { step: 'loading' } : { step: 'auth' }
  );

  const resetAndClose = useCallback(() => {
    setState(isAuthenticated ? { step: 'loading' } : { step: 'auth' });
    onClose();
  }, [onClose, isAuthenticated]);

  const loadFileList = useCallback(async () => {
    setState({ step: 'loading' });
    const result = await listFiles();
    if (result.ok) {
      setState({ step: 'file-list', files: result.data });
    } else {
      setState({ step: 'error', message: result.error });
    }
  }, []);

  const handleAuth = useCallback(async () => {
    const success = await authenticate();
    if (success) {
      await loadFileList();
    }
  }, [authenticate, loadFileList]);

  const handleSelectFile = useCallback(
    async (file: DriveFile) => {
      setState({ step: 'loading' });

      const dlResult = await downloadFile(file.id);
      if (!dlResult.ok) {
        setState({ step: 'error', message: dlResult.error });
        return;
      }

      const checkResult = checkImportConflictsFromString(dlResult.data);

      if (checkResult.status === 'error') {
        setState({
          step: 'error',
          message: checkResult.error ?? 'Error al validar el archivo',
        });
        return;
      }

      if (checkResult.status === 'conflict') {
        setState({
          step: 'conflict',
          result: checkResult,
          fileContent: dlResult.data,
        });
        return;
      }

      // No conflict — import directly
      setState({ step: 'importing' });
      const importResult = await importDataFromString(dlResult.data, 'replace');
      if (importResult.status === 'success' && importResult.imported) {
        const { transactions, categories, budgets } = importResult.imported;
        onSuccess(
          `Importadas ${transactions} transacciones, ${categories} categorías, ${budgets} presupuestos desde Drive`
        );
        resetAndClose();
      } else {
        onError(importResult.error ?? 'Error al importar');
        resetAndClose();
      }
    },
    [onSuccess, onError, resetAndClose]
  );

  const handleImport = useCallback(
    async (mode: ImportMode) => {
      if (state.step !== 'conflict') return;

      setState({ step: 'importing' });
      const result = await importDataFromString(state.fileContent, mode);

      if (result.status === 'success' && result.imported) {
        const { transactions, categories, budgets } = result.imported;
        onSuccess(
          `Importadas ${transactions} transacciones, ${categories} categorías, ${budgets} presupuestos desde Drive`
        );
        resetAndClose();
      } else {
        onError(result.error ?? 'Error al importar');
        resetAndClose();
      }
    },
    [state, onSuccess, onError, resetAndClose]
  );

  const didLoadFiles = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      didLoadFiles.current = false;
      return;
    }
    if (isAuthenticated && state.step === 'loading' && !didLoadFiles.current) {
      didLoadFiles.current = true;
      loadFileList();
    }
  }, [isOpen, isAuthenticated, state.step, loadFileList]);

  const currentStep = isAuthenticated ? state.step : 'auth';

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={resetAndClose}
      titleId="drive-load-dialog-title"
      title="Cargar desde Google Drive"
    >
      {currentStep === 'auth' && (
        <div className="space-y-4">
          <p className="text-mid-gray">
            Inicia sesión con Google para cargar tus datos desde Drive.
          </p>
          {authError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-700">{authError}</p>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 text-mid-gray hover:text-near-black transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleAuth}
              disabled={isAuthenticating}
              className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50"
            >
              {isAuthenticating ? 'Conectando...' : 'Conectar con Google'}
            </button>
          </div>
        </div>
      )}

      {(currentStep === 'loading' || currentStep === 'importing') && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          <span className="ml-3 text-mid-gray">
            {currentStep === 'importing'
              ? 'Importando...'
              : 'Cargando archivos...'}
          </span>
        </div>
      )}

      {currentStep === 'file-list' && state.step === 'file-list' && (
        <div className="space-y-4">
          {state.files.length === 0 ? (
            <p className="text-mid-gray text-center py-4">
              No hay archivos en la carpeta Budgeter.
            </p>
          ) : (
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {state.files.map((file) => (
                <li key={file.id}>
                  <button
                    onClick={() => handleSelectFile(file)}
                    className="w-full text-left px-3 py-3 hover:bg-gray-50 transition rounded"
                  >
                    <p className="text-near-black font-medium text-sm">
                      {file.name}
                    </p>
                    <p className="text-xs text-mid-gray">
                      {new Date(file.modifiedTime).toLocaleDateString('es-MX', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {file.size &&
                        ` · ${(parseInt(file.size) / 1024).toFixed(1)} KB`}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 text-mid-gray hover:text-near-black transition"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {currentStep === 'conflict' && state.step === 'conflict' && (
        <div className="space-y-4">
          <p className="text-mid-gray">
            Ya tienes datos existentes. ¿Qué deseas hacer?
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded">
              <p className="font-medium text-near-black mb-2">Datos actuales</p>
              <ul className="text-mid-gray space-y-1">
                <li>
                  {state.result.existingCounts?.transactions ?? 0} transacciones
                </li>
                <li>
                  {state.result.existingCounts?.categories ?? 0} categorías
                </li>
                <li>
                  {state.result.existingCounts?.budgets ?? 0} presupuestos
                </li>
              </ul>
            </div>
            <div className="p-3 bg-blue-50 rounded">
              <p className="font-medium text-near-black mb-2">Datos de Drive</p>
              <ul className="text-mid-gray space-y-1">
                <li>
                  {state.result.imported?.transactions ?? 0} transacciones
                </li>
                <li>{state.result.imported?.categories ?? 0} categorías</li>
                <li>{state.result.imported?.budgets ?? 0} presupuestos</li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleImport('replace')}
              className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Reemplazar todo
            </button>
            <button
              onClick={() => handleImport('merge')}
              className="w-full px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition"
            >
              Combinar datos
            </button>
            <button
              onClick={resetAndClose}
              className="w-full px-4 py-2 text-mid-gray hover:text-near-black transition"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {currentStep === 'error' && state.step === 'error' && (
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-red-700">{state.message}</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}
